import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { ProductCard } from './ProductCard';
import { Product } from '@interfaces';
import { useIntersectionObserver } from '@hooks/useIntersection';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import Image from 'next/image';
import {
  List,
  TitleContainer,
  Title,
  Subtitle,
  SeeAllContainer,
  LinkColumn,
  IconContainer,
  ForwardButton,
  BackButton,
  Column,
  Row,
  CarouselCard,
} from './Carousel.styles';
import { MainCard } from '../BaseStyles';
import { Tooltip } from '@mui/material';
import { ProductInteractionLocation } from '../../validators/Events/Product';

interface SkeletonProps {
  dataLength: number;
  title: string;
  icon?: string;
}

export const Skeleton = ({ dataLength, title, icon }: SkeletonProps) => {
  return (
    <MainCard>
      <TitleContainer>
        <Row>
          <SeeAllContainer>
            {dataLength == 1 ? (
              <Column>
                <Row>
                  {icon && (
                    <IconContainer>
                      <Image
                        src={'assets/images/home/shoppingIcon.webp'}
                        alt="icon"
                        layout="fill"
                        objectFit="contain"
                      />
                    </IconContainer>
                  )}
                  <Tooltip title={title}>
                    <Title>{title}</Title>
                  </Tooltip>
                </Row>
              </Column>
            ) : (
              <Row>
                {icon && (
                  <IconContainer>
                    <Image
                      src={icon}
                      alt="icon"
                      layout="fill"
                      objectFit="contain"
                    />
                  </IconContainer>
                )}
                <Tooltip title={title}>
                  <Column>
                    <Title>{title}</Title>
                  </Column>
                </Tooltip>
              </Row>
            )}
          </SeeAllContainer>
        </Row>
      </TitleContainer>

      <List>
        {Array.from({ length: 7 }).map((e, i) => {
          return <ProductCard.Skeleton listLength={dataLength} key={i} />;
        })}
      </List>
    </MainCard>
  );
};

export interface ProductsTypes {
  data: { product: Product; orderId?: number }[];
  loading: boolean;
  title: string;
  totalCount?: number;
  subtitle?: string;
  icon?: string;
  action?: ReactNode;
  location?: ProductInteractionLocation;
  quantityChangeCallback?: (action: 'add' | 'remove') => void; // callback function for when adding a product to cart
}

export function Carousel({
  data,
  totalCount,
  title,
  subtitle,
  icon,
  action,
  location,
  quantityChangeCallback,
}: ProductsTypes) {
  const firstRef = useRef<HTMLDivElement | null>(null);
  const lastRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const leftNavigation = useIntersectionObserver(firstRef, { threshold: 1 });
  const rightNavigation = useIntersectionObserver(lastRef, {
    threshold: 0.99,
  });

  const leftNavigationisVisible = useMemo(() => {
    if (!leftNavigation) return;
    return !leftNavigation.isIntersecting;
  }, [leftNavigation]);

  const rightNavigationisVisible = useMemo(() => {
    if (!rightNavigation) return;
    return !rightNavigation.isIntersecting;
  }, [rightNavigation]);
  const forward = useCallback(() => {
    listRef.current.scrollTo({
      left: listRef.current.scrollLeft + listRef.current.clientWidth / 3,
      behavior: 'smooth',
    });
  }, []);
  const backward = useCallback(() => {
    listRef.current.scrollTo({
      left: listRef.current.scrollLeft - listRef.current.clientWidth / 3,
      behavior: 'smooth',
    });
  }, []);

  const carouselComponents = data?.reduce(
    (acc, e) => {
      const product = e.product;
      return {
        activePriciple: acc.activePriciple || !!product.activePrinciple,
        concentrationLab:
          acc.concentrationLab ||
          !!product.concentration ||
          !!product.laboratory,
        quantity: acc.quantity || !!product.quantity,
        discount:
          acc.discount ||
          (product.discount?.price &&
            !['SOLD_OUT', 'HIDDEN', 'UNAVAILABLE'].includes(
              product.availability?.status,
            )),
        averagePrice:
          acc.averagePrice ||
          (/comprimido|cápsula/i.test(product.unit) &&
            (!!product.discount?.price || !!product.price)),
      };
    },
    {
      activePriciple: false,
      concentrationLab: false,
      quantity: false,
      discount: false,
      averagePrice: false,
    },
  );

  if (data?.length === 0) return <Skeleton dataLength={7} title={title} />;
  const maxWidth = data ? `${data.length * 200}px` : '600px';

  return (
    <CarouselCard>
      {icon ? (
        <TitleContainer maxWidth={maxWidth}>
          <Row>
            <SeeAllContainer>
              {totalCount == 1 ? (
                <Column>
                  <Row>
                    <IconContainer>
                      <Image
                        src={icon}
                        alt="icon"
                        layout="fill"
                        objectFit="contain"
                      />
                    </IconContainer>
                    <Tooltip title={title}>
                      <Title variant="h2">{title}</Title>
                    </Tooltip>
                  </Row>
                  {subtitle && <Subtitle addMarginTop>{subtitle}</Subtitle>}
                </Column>
              ) : (
                <Row>
                  <IconContainer>
                    <Image
                      src={icon}
                      alt="icon"
                      layout="fill"
                      objectFit="contain"
                    />
                  </IconContainer>
                  <Column>
                    <Tooltip title={title}>
                      <Title variant="h2">{title}</Title>
                    </Tooltip>
                    {subtitle && <Subtitle>{subtitle}</Subtitle>}
                  </Column>
                </Row>
              )}
              <LinkColumn>{action}</LinkColumn>
            </SeeAllContainer>
          </Row>
        </TitleContainer>
      ) : (
        <TitleContainer maxWidth={maxWidth}>
          <SeeAllContainer>
            <Tooltip title={title}>
              <Title variant="h2">{title}</Title>
            </Tooltip>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            <LinkColumn>{action}</LinkColumn>
          </SeeAllContainer>
        </TitleContainer>
      )}
      {leftNavigationisVisible && (
        <BackButton onClick={backward}>
          <ArrowForwardIosRoundedIcon color="primary" />
        </BackButton>
      )}
      {rightNavigationisVisible && (
        <ForwardButton onClick={forward}>
          <ArrowForwardIosRoundedIcon color="primary" />
        </ForwardButton>
      )}
      <List ref={listRef}>
        {data?.map((element, i, array) => {
          const product = element?.product;
          return product ? (
            <ProductCard
              key={product?.id}
              product={product}
              index={i}
              listLength={array.length}
              inCarousel={true}
              location={location}
              quantityChangeCallback={quantityChangeCallback}
              carouselComponents={carouselComponents}
              ref={i == array.length - 1 ? lastRef : i == 0 ? firstRef : null}
            />
          ) : null;
        })}
      </List>
    </CarouselCard>
  );
}

Carousel.Skeleton = Skeleton;
